import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, FileText, Shield, Check, AlertCircle } from 'lucide-react';

interface TermsAcceptanceModalProps {
  isOpen: boolean;
  onAccept: (acceptedTerms: boolean, acceptedPrivacy: boolean) => void;
  onClose: () => void;
  isRequired?: boolean;
}

const TermsAcceptanceModal: React.FC<TermsAcceptanceModalProps> = ({
  isOpen,
  onAccept,
  onClose,
  isRequired = true
}) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleSubmit = () => {
    if (isRequired && (!acceptedTerms || !acceptedPrivacy)) {
      setShowError(true);
      return;
    }
    
    setShowError(false);
    onAccept(acceptedTerms, acceptedPrivacy);
  };

  const handleClose = () => {
    if (!isRequired) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Shield className="text-blue-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">
              Legal Agreement Required
            </h2>
          </div>
          {!isRequired && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="space-y-6">
            
            {/* Introduction */}
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="text-blue-600" size={32} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Welcome to Evoltbay!
              </h3>
              <p className="text-gray-600">
                To continue using our solar energy marketplace, please review and accept our 
                Terms of Service and Privacy Policy.
              </p>
            </div>

            {/* Error Message */}
            {showError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <h4 className="font-medium text-red-800">Agreement Required</h4>
                  <p className="text-red-700 text-sm mt-1">
                    You must accept both the Terms of Service and Privacy Policy to continue.
                  </p>
                </div>
              </div>
            )}

            {/* Document Links */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="text-blue-600" size={20} />
                  <h4 className="font-medium text-gray-900">Terms of Service</h4>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Our terms outline the rules and regulations for using Evoltbay's marketplace platform.
                </p>
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Read Terms of Service →
                </Link>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="text-blue-600" size={20} />
                  <h4 className="font-medium text-gray-900">Privacy Policy</h4>
                </div>
                <p className="text-gray-600 text-sm mb-3">
                  Learn how we collect, use, and protect your personal information.
                </p>
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Read Privacy Policy →
                </Link>
              </div>
            </div>

            {/* Acceptance Checkboxes */}
            <div className="space-y-4 bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900">Your Consent</h4>
              
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative flex-shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    acceptedTerms 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'border-gray-300 group-hover:border-blue-400'
                  }`}>
                    {acceptedTerms && <Check className="text-white" size={14} />}
                  </div>
                </div>
                <div className="text-sm">
                  <span className="text-gray-700">
                    I have read and agree to the{' '}
                    <Link 
                      to="/terms" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Terms of Service
                    </Link>
                  </span>
                  {isRequired && <span className="text-red-500 ml-1">*</span>}
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative flex-shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={acceptedPrivacy}
                    onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    acceptedPrivacy 
                      ? 'bg-blue-600 border-blue-600' 
                      : 'border-gray-300 group-hover:border-blue-400'
                  }`}>
                    {acceptedPrivacy && <Check className="text-white" size={14} />}
                  </div>
                </div>
                <div className="text-sm">
                  <span className="text-gray-700">
                    I have read and agree to the{' '}
                    <Link 
                      to="/privacy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Privacy Policy
                    </Link>
                  </span>
                  {isRequired && <span className="text-red-500 ml-1">*</span>}
                </div>
              </label>
            </div>

            {/* Additional Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">What this means:</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• You agree to follow our platform rules and guidelines</li>
                <li>• You understand how we handle your personal information</li>
                <li>• You can withdraw consent at any time by contacting us</li>
                <li>• These agreements help protect both you and other users</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            {!isRequired && (
              <button
                onClick={handleClose}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={isRequired && (!acceptedTerms || !acceptedPrivacy)}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                isRequired && (!acceptedTerms || !acceptedPrivacy)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isRequired ? 'Accept and Continue' : 'Save Preferences'}
            </button>
          </div>
          
          {isRequired && (
            <p className="text-xs text-gray-500 mt-3 text-center">
              <span className="text-red-500">*</span> Required fields. 
              You must accept both agreements to use Evoltbay.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal; 